import { useCallback, useEffect, useRef, useState } from 'react';
import type { EmailPrefs, SummaryEmailPayload, SummaryEmailResult, SummaryEmailStatus } from '../../global';

const UNKNOWN: SummaryEmailStatus = {
  enabled: true,
  everyone: false,
  recipient: '',
  canSend: false,
};

/**
 * Özet e-postası durumu ve gönderimi.
 *
 * Durum ana süreçten geliyor: açık mı ve hangi adrese gideceği. Gönderim
 * IPC'nin arkasında, oradan sunucuya (kendi alan adımızdan) gidiyor.
 */
export function useSummaryEmail(enabled: boolean) {
  const [status, setStatus] = useState<SummaryEmailStatus>(UNKNOWN);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isSending, setIsSending] = useState(false);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    try {
      const next = await window.zabit.getSummaryEmailStatus();
      if (mountedRef.current) setStatus(next);
    } catch {
      // Durum okunamadıysa varsayılanla devam — ekran kilitlenmesin.
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  const savePrefs = useCallback(async (patch: Partial<EmailPrefs>) => {
    // İyimser: anahtar tek bir boolean/metin, geri alma maliyeti düşük.
    setStatus((prev) => ({
      ...prev,
      enabled: patch.summaryEmailEnabled ?? prev.enabled,
      everyone: patch.summaryEmailEveryone ?? prev.everyone,
    }));
    try {
      await window.zabit.saveSummaryEmailPrefs(patch);
    } finally {
      await load();
    }
  }, [load]);

  const send = useCallback(
    /** `meetingId`: kaydedilmiş toplantı — "Herkese" açıksa katılımcılar ondan okunur. */
    async (payload: SummaryEmailPayload, force = false, meetingId?: string): Promise<SummaryEmailResult> => {
      setIsSending(true);
      try {
        return await window.zabit.sendSummaryEmail(payload, force, meetingId);
      } catch (err) {
        return { ok: false, reason: 'network', message: (err as Error).message };
      } finally {
        if (mountedRef.current) setIsSending(false);
      }
    },
    []
  );

  return { status, isLoading, isSending, savePrefs, send, reload: load };
}
